import type { PatchErrandResponse } from '../hooks/use-patch-errand';
import type { ErrandFileFormValue, ErrandActionFormValue } from '../types';

interface PresignedPost {
  url: string;
  fields: Record<string, string>;
}

/**
 * Upload a single file to S3 using a presigned POST URL.
 */
async function uploadToS3(file: File, presigned: PresignedPost): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(presigned.fields)) {
    formData.append(key, value);
  }
  formData.append('file', file);

  const res = await fetch(presigned.url, {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    throw new Error(`S3 upload failed for file "${file.name}": ${res.status}`);
  }
}

/**
 * After a successful PATCH, upload any new errand-level files using the
 * presigned URLs returned in the response.
 *
 * The response `errand_files` array aligns positionally with the new files
 * in the payload (entries without an `id` are new). Existing files have
 * `file_obj.post === null`.
 */
export async function uploadErrandFiles(
  errandFiles: ErrandFileFormValue[],
  responseFiles: PatchErrandResponse['errand_files']
): Promise<void> {
  // Only new files (those with a File object) need uploading
  const newFiles = errandFiles.filter((f) => f.file instanceof File);

  // Response entries with a presigned post URL correspond to new files
  const responseNewFiles = responseFiles.filter((r) => r.file_obj.post !== null);

  const uploads: Promise<void>[] = [];

  if (newFiles.length !== responseNewFiles.length) {
    throw new Error(
      `Presigned URL count mismatch: expected ${newFiles.length}, got ${responseNewFiles.length}`
    );
  }

  for (let i = 0; i < newFiles.length; i++) {
    const formFile = newFiles[i];
    const responseEntry = responseNewFiles[i];

    if (!formFile?.file) {
      throw new Error(`Missing file object for errand file at index ${i}`);
    }
    if (!responseEntry?.file_obj?.post) {
      throw new Error(`Missing presigned URL for errand file "${formFile.file.name}"`);
    }

    uploads.push(uploadToS3(formFile.file, responseEntry.file_obj.post));
  }

  await Promise.all(uploads);
}

/**
 * After a successful PATCH, upload any new action-level files using the
 * presigned URLs returned in the response.
 *
 * For each action, the response `errand_action_files` entries belonging to
 * that action (matched by `errand_action.id`) and having `file_obj.post`
 * non-null correspond positionally to new files in that action's `action_files`.
 */
export async function uploadErrandActionFiles(
  errandActions: ErrandActionFormValue[],
  responseActionFiles: PatchErrandResponse['errand_action_files']
): Promise<void> {
  // All response entries with a presigned post URL need uploading, in the
  // same order as the new files were submitted across all actions.
  const presignedQueue = responseActionFiles.filter(
    (r) => r.file_obj.post !== null
  );

  const newFiles: File[] = [];
  for (const action of errandActions) {
    for (const af of action.action_files ?? []) {
      if (af.file instanceof File) {
        newFiles.push(af.file);
      }
    }
  }

  if (newFiles.length !== presignedQueue.length) {
    throw new Error(
      `Presigned URL count mismatch for action files: expected ${newFiles.length}, got ${presignedQueue.length}`
    );
  }

  const uploads: Promise<void>[] = [];
  for (let i = 0; i < newFiles.length; i++) {
    const file = newFiles[i]!;
    const responseEntry = presignedQueue[i]!;

    if (!responseEntry.file_obj.post) {
      throw new Error(`Missing presigned URL for action file "${file.name}"`);
    }

    uploads.push(uploadToS3(file, responseEntry.file_obj.post));
  }

  await Promise.all(uploads);
}
